import {
  Checkbox,
  RowRenderResult,
  Table,
  TableColumnDefinition,
  TableContext,
  TableDefinition,
  Typography,
} from '@garner-health/components-common';
import { SR, useStringResource } from '@garner-health/lib-ui-content-management';
import { useFlags } from '@garner-health/lib-ui-feature-flags';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { ProviderResultItem, ProvidersResponseSetType } from '~/clients';
import { useSpecialtyLabel } from '~/contexts';
import { getProfessionalName, providerKeyResolver, roundToDecimals } from '~/util';

import { encodeProviderIdForReactHookForm, MAX_PROVIDER_ROW_SELECTIONS } from './common';
import { useSelectedProviderIds } from './use-provider-selections';

export type ProvidersListProps = {
  searchType: ProvidersResponseSetType;
  providers: ProviderResultItem[];
  configureRow?: (item: ProviderResultItem, key: string) => RowRenderResult;
  selectedKey?: string;
};

function getProviderName(item: ProviderResultItem) {
  if (item.firstName || item.lastName) return getProfessionalName(item);
  return item.organizationName;
}

export const ProvidersTable = ({ searchType, providers, configureRow, selectedKey }: ProvidersListProps) => {
  const { showProviderAffiliations } = useFlags();
  const specialtyLabel = useSpecialtyLabel();
  const formContext = useFormContext();
  const { selectedProviderInfo, isProviderResultSelected } = useSelectedProviderIds(formContext);

  const columns: TableColumnDefinition<ProviderResultItem>[] = [
    {
      key: 'checkbox',
      label: '',
      isCheckbox: true,
      render: item => {
        const disabled = selectedProviderInfo.length >= MAX_PROVIDER_ROW_SELECTIONS && !isProviderResultSelected(item);
        const encodedProviderId = encodeProviderIdForReactHookForm(
          item.id,
          item.type === 'facility' ? item.location.id : undefined,
        );

        return <Checkbox name={`checkbox-${encodedProviderId}`} disabled={disabled} />;
      },
    },
    {
      key: 'nameAndSpecialty',
      label: useStringResource('search', 'providerNameAndSpecialtyColumnLabel'),
      render: item => {
        const specialties = item.specialties.map(specialtyLabel).join(', ');
        return (
          <>
            <Typography variant="body1">{getProviderName(item)}</Typography>

            <Typography variant="body1" color="common.muted">
              {specialties}
            </Typography>

            {!!(showProviderAffiliations && item.providerCorporationName) && (
              <Typography variant="body2" color="common.muted">
                {item.providerCorporationName}
              </Typography>
            )}
          </>
        );
      },
    },
    {
      key: 'distance',
      label: useStringResource('search', 'providerDistanceColumnLabel'),
      render: item => {
        const distance = item.distanceMi;
        if (distance == null) return null;

        return (
          <SR
            package="search"
            id="providerDistanceMilesText"
            interpolate={{
              distance: () => roundToDecimals(distance, 2),
            }}
          />
        );
      },
    },
  ];

  const providerOverallScoreLabel = useStringResource('search', 'providerOverallScoreLabel');
  const notPreferredText = useStringResource('search', 'notPreferredText');
  if (searchType !== 'facility' && searchType !== 'hcsc_compliant') {
    columns.push({
      key: 'overallScore',
      label: providerOverallScoreLabel,
      render: item => {
        return item.overallScore ? (
          <Typography variant="button" color="garner.primary.dark">
            {item.overallScore}
          </Typography>
        ) : (
          <Typography variant="body2">{notPreferredText}</Typography>
        );
      },
    });
  }

  const tableDefinition: TableDefinition<ProviderResultItem> = {
    columns,
    keyResolver: providerKeyResolver,
  };

  const tableContext: TableContext<ProviderResultItem> = {
    definition: tableDefinition,
    items: providers,
  };

  return <Table context={tableContext} configureRow={configureRow} selectedKey={selectedKey} />;
};
